<script setup lang="ts">
import {
  BadgeComponent,
  ConfirmationPopup,
  getSocialNetwork,
  PlatformIconComponent,
  type SocialItemEnum,
  socialsDataMapper,
} from '@metapic-apps/shared'
import type { FormInstance, FormRules } from 'element-plus'
import { ElForm, ElFormItem, ElIcon } from 'element-plus'
import { computed, reactive, ref, watch } from 'vue'

import {
  useAddCreatorSocialProfile,
  useDeleteCreatorSocialProfile,
  useSetMainCreatorSocialProfile,
} from '@/api/creator-social-profiles.api'
import AddIcon from '@/assets/icons/add-plus.svg?component'
import DeleteIcon from '@/assets/icons/delete.svg?component'
import { notification } from '@/lib/helpers'
import type { CreatorDetail, CreatorSocialProfile } from '@/lib/types/creator-detail.types'
import {
  BoxComponent,
  ButtonComponent,
  DrawerComponent,
  FlexComponent,
  InputComponent,
  SelectComponent,
  TypographyComponent,
} from '@/ui-library'

const props = defineProps<{ creator: CreatorDetail }>()
const isOpen = defineModel<boolean>({ default: false })
const emits = defineEmits<{ changed: [] }>()
const { addCreatorSocialProfile, isLoading: isAdding } = useAddCreatorSocialProfile()
const { deleteCreatorSocialProfile, isLoading: isDeleting } = useDeleteCreatorSocialProfile()
const { setMainCreatorSocialProfile, isLoading: isSettingMain } = useSetMainCreatorSocialProfile()
const profiles = ref<CreatorSocialProfile[]>([])
const pendingProfileId = ref<string | null>(null)
const isLoading = computed(() => isAdding.value || isDeleting.value || isSettingMain.value)

const resetProfiles = () => {
  pendingProfileId.value = null
  profiles.value = props.creator.socials.map((profile) => ({ ...profile }))
}

type FormData = {
  platform: Exclude<SocialItemEnum, SocialItemEnum.Zezam> | undefined
  handle: string
}

const ruleFormRef = ref<FormInstance>()
const formData = reactive<FormData>({ platform: undefined, handle: '' })
const rules = reactive<FormRules<FormData>>({
  platform: [{ required: true, message: 'Please select a social network', trigger: 'change' }],
  handle: [{ required: true, whitespace: true, message: 'Please enter a handle or URL', trigger: 'blur' }],
})

const resetForm = () => {
  if (ruleFormRef.value) {
    ruleFormRef.value.resetFields()
    return
  }
  formData.platform = undefined
  formData.handle = ''
}
const isAddingProfile = ref(false)
const isDeleteConfirmationOpen = ref(false)
const profileToDeleteId = ref<string | null>(null)
const profileToDelete = computed(() => profiles.value.find((profile) => profile.id === profileToDeleteId.value))
const deleteDescription = computed(() => {
  const profile = profileToDelete.value
  if (!profile) {
    return ''
  }
  const platform = getSocialNetwork(profile.type)?.title ?? 'Website'
  return `Are you sure you want to delete the ${platform} profile "${profile.identifier}"?`
})

const socialTypeOptions = Object.entries(socialsDataMapper).map(([key, value]) => ({
  label: value.title,
  value: key,
  icon: value.icon,
}))
const selectedSocialType = computed(() => (formData.platform ? getSocialNetwork(formData.platform) : undefined))

watch(
  isOpen,
  (open) => {
    resetForm()
    isAddingProfile.value = false
    isDeleteConfirmationOpen.value = false
    profileToDeleteId.value = null
    if (open) {
      resetProfiles()
    }
  },
  { immediate: true },
)

const addSocialProfile = async () => {
  const valid = await ruleFormRef.value?.validate().catch(() => false)
  const type = formData.platform
  const handle = formData.handle.trim()
  if (!valid || isLoading.value || !type) {
    return
  }
  const created = await addCreatorSocialProfile({
    userId: String(props.creator.id),
    platform: type,
    handle,
  })
  if (!created) {
    return
  }
  profiles.value = [...profiles.value.map((profile) => (created.isPrimary ? { ...profile, isPrimary: false } : profile)), created]
  resetForm()
  notification.success('Social profile added')
  emits('changed')
}

const isDeleteDisabled = (social: CreatorSocialProfile): boolean => isLoading.value || social.isConnected || social.isPrimary

const requestDelete = (social: CreatorSocialProfile) => {
  profileToDeleteId.value = social.id
  isDeleteConfirmationOpen.value = true
}

const confirmDelete = async () => {
  const social = profileToDelete.value
  if (!social) {
    return
  }
  isDeleteConfirmationOpen.value = false
  profileToDeleteId.value = null
  pendingProfileId.value = social.id
  try {
    const success = await deleteCreatorSocialProfile(String(props.creator.id), social.id)
    if (!success) {
      return
    }
    profiles.value = profiles.value.filter((item) => item.id !== social.id)
    notification.success('Social profile deleted')
    emits('changed')
  } finally {
    pendingProfileId.value = null
  }
}

const selectMain = async (id: string) => {
  pendingProfileId.value = id
  try {
    const updated = await setMainCreatorSocialProfile(String(props.creator.id), id)
    if (!updated) {
      return
    }
    profiles.value = profiles.value.map((item) => {
      if (item.id === id) {
        return updated
      }
      return updated.isPrimary ? { ...item, isPrimary: false } : item
    })
    notification.success('Main profile updated')
    emits('changed')
  } finally {
    pendingProfileId.value = null
  }
}
</script>
<template>
  <DrawerComponent
    v-model="isOpen"
    :with-header="true"
    size="640"
    :close-on-click-modal="!isLoading"
    :close-on-press-escape="!isLoading"
    :show-close="!isLoading"
  >
    <template #header>
      <TypographyComponent variant="headline-medium">Social media profiles</TypographyComponent>
    </template>
    <FlexComponent direction="column" :gap="32" class="socials-drawer" :aria-busy="isLoading">
      <FlexComponent direction="column" :gap="8" class="socials-drawer__profiles">
        <FlexComponent v-for="social in profiles" :key="social.id" align="center" :gap="8" class="socials-drawer__profile-row">
          <FlexComponent align="center" :gap="16" class="socials-drawer__input">
            <PlatformIconComponent :platform="social.type" :size="24" :is-connected="social.isConnected" class="socials-drawer__icon" />
            <TypographyComponent variant="body-large" class="socials-drawer__identifier">
              {{ social.identifier }}
            </TypographyComponent>
            <BadgeComponent v-if="social.isPrimary" variant="accent" size="small"> Main </BadgeComponent>
            <ButtonComponent
              v-else
              variant="text"
              size="small"
              class="socials-drawer__set-main"
              :class="{ 'socials-drawer__set-main--pending': isSettingMain && pendingProfileId === social.id }"
              :is-loading="isSettingMain && pendingProfileId === social.id"
              :is-disabled="isLoading"
              :aria-label="`Set ${social.identifier} as main profile`"
              @click.stop="selectMain(social.id)"
            >
              Set as main profile
            </ButtonComponent>
          </FlexComponent>
          <ButtonComponent
            variant="transparent"
            is-icon
            class="socials-drawer__delete"
            :icon-color="isDeleteDisabled(social) ? 'var(--secondary-disabled)' : 'var(--error)'"
            :is-disabled="isDeleteDisabled(social)"
            :is-loading="isDeleting && pendingProfileId === social.id"
            :aria-label="`Delete ${social.identifier} profile`"
            @click="requestDelete(social)"
          >
            <DeleteIcon />
          </ButtonComponent>
        </FlexComponent>
        <template v-if="isAddingProfile">
          <ElForm
            ref="ruleFormRef"
            :model="formData"
            :rules="rules"
            hide-required-asterisk
            :validate-on-rule-change="false"
            inline-message
            class="socials-drawer__add-form"
            @submit.prevent="addSocialProfile"
          >
            <FlexComponent :gap="8" align="center">
              <ElFormItem prop="platform" class="socials-drawer__type-select">
                <SelectComponent
                  v-model="formData.platform"
                  :disabled="isLoading"
                  :options="socialTypeOptions"
                  placeholder="Social network"
                  :teleported="false"
                >
                  <template #option="{ item }">
                    <FlexComponent align="center" :gap="8">
                      <ElIcon :size="20">
                        <component :is="item.icon" />
                      </ElIcon>
                      <span>{{ item.label }}</span>
                    </FlexComponent>
                  </template>
                  <template v-if="selectedSocialType" #label>
                    <FlexComponent align="center" :gap="8">
                      <ElIcon :size="20">
                        <component :is="selectedSocialType.icon" />
                      </ElIcon>
                      <span>{{ selectedSocialType.title }}</span>
                    </FlexComponent>
                  </template>
                </SelectComponent>
              </ElFormItem>
              <ElFormItem prop="handle" class="socials-drawer__add-identifier">
                <InputComponent v-model="formData.handle" :is-disabled="isLoading" placeholder="Handle or URL" />
              </ElFormItem>
              <ButtonComponent
                variant="primary"
                :is-loading="isAdding"
                :is-disabled="isLoading"
                class="socials-drawer__submit"
                @click="addSocialProfile"
              >
                Add
              </ButtonComponent>
            </FlexComponent>
          </ElForm>
        </template>
        <BoxComponent class="socials-drawer__add-btn">
          <ButtonComponent variant="text" :is-disabled="isLoading" @click="isAddingProfile = true">
            <template #iconLeft>
              <ElIcon :size="18">
                <AddIcon />
              </ElIcon>
            </template>
            Add profile
          </ButtonComponent>
        </BoxComponent>
      </FlexComponent>
    </FlexComponent>
  </DrawerComponent>
  <ConfirmationPopup
    v-if="isDeleteConfirmationOpen"
    v-model="isDeleteConfirmationOpen"
    title="Delete social profile"
    :description="deleteDescription"
    submit-btn-text="Delete"
    submit-btn-variant="danger"
    @on-submit="confirmDelete"
  />
</template>
<style scoped lang="scss">
.socials-drawer {
  height: 100%;
}

.socials-drawer__profiles {
  flex: 1;
  overflow-y: auto;
}

:deep(.socials-drawer__profile-row:hover .socials-drawer__set-main),
:deep(.socials-drawer__set-main:focus-visible),
:deep(.socials-drawer__set-main--pending) {
  opacity: 1;
  pointer-events: auto;
}

.socials-drawer__set-main {
  flex-shrink: 0;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease;

  @media (hover: none) {
    opacity: 1;
    pointer-events: auto;
  }
}

:deep(.socials-drawer__set-main.button) {
  min-height: 20px;
  padding: 2px 8px;
  border: 0;
  border-radius: 24px;
  background: var(--button-hover);
  color: var(--primary);
  font-size: 11px;
  line-height: 16px;
  font-weight: 600;
  white-space: nowrap;
}

.socials-drawer__input {
  flex: 1;
  min-width: 0;
  padding: 16px;
  border: 1px solid rgba(34, 34, 34, 0.12);
  border-radius: 8px;
}

.socials-drawer__identifier {
  flex: 1;
  min-width: 0;
}

.socials-drawer__icon {
  flex-shrink: 0;
}

.socials-drawer__delete.button {
  :deep(svg path) {
    fill: currentColor;
  }

  &:disabled {
    color: var(--secondary-disabled);
    cursor: not-allowed;
  }
}

.socials-drawer__add-form {
  padding-top: 8px;

  :deep(.el-form-item) {
    margin-bottom: 0;
  }

  :deep(.el-form-item__content) {
    display: block;
    width: 100%;
  }

  :deep(.el-form-item__error) {
    display: block;
    margin-left: 0;
  }
}

.socials-drawer__type-select {
  width: 180px;
  flex-shrink: 0;
}

.socials-drawer__add-identifier {
  flex: 1;
  min-width: 0;
}

.socials-drawer__add-btn {
  text-align: left;
}

:deep(.el-icon path) {
  fill: currentColor;
}
</style>
